import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";

@customElement("create-flag-dialog")
export class CreateFlagDialog extends LitElement {
  static styles = css`
    :host { display: block; font-family: Geist, sans-serif; }
    .form-field { margin-bottom: var(--spacing-16, 16px); }
    .form-field label {
      display: block;
      font-size: 13px;
      font-weight: 600;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      margin-bottom: var(--spacing-4, 4px);
    }
    .form-field .help {
      font-size: 11px;
      color: var(--colors-foreground-fg-gray-500, #98a2b3);
      margin-top: var(--spacing-4, 4px);
    }
    .modal-footer { display: flex; gap: var(--spacing-8, 8px); justify-content: flex-end; }
    bee-alert { margin-bottom: var(--spacing-16, 16px); }
  `;

  @property({ type: Boolean }) open = false;
  @property() product: string | null = null;

  @state() private _key = "";
  @state() private _description = "";
  @state() private _applying = false;
  @state() private _error = "";

  private get _isValid(): boolean {
    return !!this.product && /^[a-z0-9][a-z0-9-]*$/.test(this._key);
  }

  private _close() {
    this._key = "";
    this._description = "";
    this._error = "";
    this._applying = false;
    this.dispatchEvent(new CustomEvent("close"));
  }

  private async _submit() {
    if (!this._isValid || this._applying || !this.product) return;
    this._applying = true;
    this._error = "";
    try {
      await api.createFlag({ product: this.product, key: this._key, description: this._description });
      this.dispatchEvent(new CustomEvent("flag-created", { detail: { key: this._key } }));
      this._close();
    } catch (e: unknown) {
      this._error = e instanceof Error ? e.message : "Failed to create flag";
    } finally {
      this._applying = false;
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <bee-modal open in-root title="Create Flag" @bee-request-close=${this._close}>
        <div class="form-field">
          <label>Flag Key</label>
          <bee-input
            placeholder="e.g., new-viewer"
            .value=${this._key}
            @bee-input=${(e: Event) => { this._key = (e.target as HTMLInputElement).value.trim().toLowerCase(); }}
          ></bee-input>
          <div class="help">Lowercase alphanumeric with hyphens. Created off (fail-closed) in dev, stage, and prod.</div>
        </div>

        <div class="form-field">
          <label>Description (optional)</label>
          <bee-input
            placeholder="What this flag gates"
            .value=${this._description}
            @bee-input=${(e: Event) => { this._description = (e.target as HTMLInputElement).value; }}
          ></bee-input>
        </div>

        ${this._error ? html`<bee-alert variant="error" open><span slot="message">${this._error}</span></bee-alert>` : nothing}

        <div slot="footer" class="modal-footer">
          <bee-button label="Cancel" bee-type="secondary-grey" @click=${this._close} ?disabled=${this._applying}></bee-button>
          <bee-button
            label=${this._applying ? "Creating..." : "Create Flag"}
            bee-type="primary"
            @click=${this._submit}
            ?disabled=${!this._isValid || this._applying}
            ?loading=${this._applying}
          ></bee-button>
        </div>
      </bee-modal>
    `;
  }
}
